// Practice CRM: a Clients view (your accounting clients = projects, with billing
// pulled from invoices) and a Pipeline of prospective clients (leads).
import React, { useState, useEffect, useMemo } from 'react';
import { loadInvoices, saveProjectCrm, loadLeads, saveLead, deleteLead } from './firebase';
import { fmtMoney } from './insights';

const inp = { width: '100%', padding: '7px 9px', fontSize: 12, border: '1px solid #e0ddd8', borderRadius: 5, boxSizing: 'border-box', marginTop: 3 };
const btnPrimary = { padding: '8px 14px', borderRadius: 6, border: 'none', background: '#1a1a2e', color: '#c8a24e', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const btnGhost = { padding: '8px 14px', borderRadius: 6, border: '1px solid #e0ddd8', background: '#fff', color: '#444', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const miniBtn = { fontSize: 10, padding: '2px 6px', border: '1px solid #e0ddd8', borderRadius: 4, background: '#fff', cursor: 'pointer', color: '#666' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 };
const modal = { background: '#fff', borderRadius: 10, padding: 18, width: 360, maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto' };

function Stat({ label, value }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #eceae4', borderRadius: 8, padding: '10px 14px', minWidth: 120 }}>
      <div style={{ fontSize: 10, color: '#9a9488', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', marginTop: 2 }}>{value}</div>
    </div>
  );
}
function Field({ label, children }) {
  return <div style={{ marginBottom: 8 }}><label style={{ fontSize: 11, color: '#7a7568', fontWeight: 600 }}>{label}</label>{children}</div>;
}

// ── Clients ──
const STATUS = [
  { key: 'lead', label: 'Lead', color: '#3d567a' },
  { key: 'onboarding', label: 'Onboarding', color: '#9a7d3d' },
  { key: 'active', label: 'Active', color: '#3d7a52' },
  { key: 'at-risk', label: 'At risk', color: '#b3623d' },
  { key: 'churned', label: 'Churned', color: '#b33' },
];
const statusMeta = (k) => STATUS.find((s) => s.key === k) || STATUS[2];

function billingByProject(invoices) {
  const map = {};
  (invoices || []).forEach((i) => {
    const pid = i.customerProjectId;
    if (!pid) return;
    const m = map[pid] || (map[pid] = { billed: 0, paid: 0, outstanding: 0, lastDate: '', count: 0 });
    const tot = Number(i.total) || 0;
    m.billed += tot; m.count += 1;
    if (i.status === 'paid') m.paid += tot;
    else if (i.status !== 'cancelled' && i.status !== 'draft') m.outstanding += tot;
    if (i.date && i.date > m.lastDate) m.lastDate = i.date;
  });
  return map;
}

export function ClientsView({ projects, onChanged }) {
  const [invoices, setInvoices] = useState([]);
  const [sel, setSel] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { loadInvoices().then(setInvoices).catch(() => {}); }, []);

  const bills = useMemo(() => billingByProject(invoices), [invoices]);
  const totalOutstanding = Object.values(bills).reduce((s, b) => s + b.outstanding, 0);
  const activeCount = (projects || []).filter((p) => ((p.crm && p.crm.status) || 'active') === 'active').length;
  const selProject = (projects || []).find((p) => p.id === sel);

  function openEdit(p) {
    setSel(p.id);
    setDraft({ status: 'active', contact: '', email: '', phone: '', notes: '', ...(p.crm || {}) });
  }
  async function save() {
    if (!sel) return;
    setBusy(true);
    try { await saveProjectCrm(sel, draft); if (onChanged) await onChanged(); setSel(null); setDraft(null); }
    catch (e) { alert('Could not save: ' + e.message); }
    setBusy(false);
  }

  return (
    <div>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>Clients</div>
      <div style={{ fontSize: 11, color: '#9a9488', marginTop: 2, marginBottom: 14 }}>Your accounting clients, their relationship status and what they owe the practice.</div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <Stat label="Clients" value={(projects || []).length} />
        <Stat label="Active" value={activeCount} />
        <Stat label="Outstanding (AR)" value={fmtMoney(totalOutstanding)} />
      </div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 520px', minWidth: 340, background: '#fff', border: '1px solid #eceae4', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#f4f2ed', color: '#7a7568', textAlign: 'left' }}>
              <th style={{ padding: '8px 10px' }}>Client</th><th style={{ padding: '8px 10px' }}>Status</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>Billed</th><th style={{ padding: '8px 10px', textAlign: 'right' }}>Outstanding</th>
              <th style={{ padding: '8px 10px' }}>Last invoice</th>
            </tr></thead>
            <tbody>
              {(projects || []).map((p) => {
                const b = bills[p.id] || { billed: 0, outstanding: 0, lastDate: '' };
                const sm = statusMeta((p.crm && p.crm.status) || 'active');
                return (
                  <tr key={p.id} onClick={() => openEdit(p)} style={{ borderTop: '1px solid #f0eeea', cursor: 'pointer', background: sel === p.id ? '#faf7f0' : '#fff' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 600, color: '#1a1a2e' }}>{p.name}<div style={{ fontSize: 10, color: '#9a9488', fontWeight: 400 }}>{p.type || ''}</div></td>
                    <td style={{ padding: '8px 10px' }}><span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: sm.color + '22', color: sm.color, fontWeight: 600 }}>{sm.label}</span></td>
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>{fmtMoney(b.billed)}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: b.outstanding > 0 ? '#b33' : '#1a1a2e' }}>{fmtMoney(b.outstanding)}</td>
                    <td style={{ padding: '8px 10px', color: '#666' }}>{b.lastDate || '—'}</td>
                  </tr>
                );
              })}
              {(projects || []).length === 0 && <tr><td colSpan={5} style={{ padding: 14, color: '#9a9488' }}>No client companies yet.</td></tr>}
            </tbody>
          </table>
        </div>
        {selProject && draft && (
          <div style={{ flex: '0 1 320px', minWidth: 280, background: '#fff', border: '1px solid #eceae4', borderRadius: 8, padding: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>{selProject.name}</div>
            <Field label="Status">
              <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} style={inp}>
                {STATUS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Primary contact"><input value={draft.contact} onChange={(e) => setDraft({ ...draft, contact: e.target.value })} style={inp} /></Field>
            <Field label="Email"><input value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} style={inp} /></Field>
            <Field label="Phone"><input value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} style={inp} /></Field>
            <Field label="Notes"><textarea value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} rows={4} style={{ ...inp, resize: 'vertical' }} /></Field>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button disabled={busy} onClick={save} style={btnPrimary}>{busy ? 'Saving…' : 'Save'}</button>
              <button onClick={() => { setSel(null); setDraft(null); }} style={btnGhost}>Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Pipeline ──
const STAGES = ['Lead', 'Contacted', 'Proposal', 'Won', 'Lost'];
const STAGE_COLOR = { Lead: '#3d567a', Contacted: '#9a7d3d', Proposal: '#c8a24e', Won: '#3d7a52', Lost: '#b33' };

export function PipelineView() {
  const [leads, setLeads] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = () => loadLeads().then(setLeads).catch(() => {});
  useEffect(() => { reload(); }, []);

  async function save(l) {
    setBusy(true);
    try { await saveLead(l); await reload(); setEditing(null); } catch (e) { alert('Could not save: ' + e.message); }
    setBusy(false);
  }
  async function remove(id) {
    if (!window.confirm('Delete this lead?')) return;
    try { await deleteLead(id); await reload(); } catch (e) { alert(e.message); }
  }
  async function move(l, stage) { try { await saveLead({ ...l, stage }); reload(); } catch (e) { alert(e.message); } }

  const openValue = leads.filter((l) => l.stage !== 'Lost' && l.stage !== 'Won').reduce((s, l) => s + (Number(l.value) || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>Pipeline</div>
          <div style={{ fontSize: 11, color: '#9a9488', marginTop: 2 }}>Prospective clients · open pipeline value {fmtMoney(openValue)}/mo</div>
        </div>
        <button onClick={() => setEditing({ stage: 'Lead' })} style={btnPrimary}>+ Add lead</button>
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 14, overflowX: 'auto', paddingBottom: 8 }}>
        {STAGES.map((stage) => {
          const items = leads.filter((l) => (l.stage || 'Lead') === stage);
          return (
            <div key={stage} style={{ flex: '0 0 230px', background: '#faf9f6', border: '1px solid #eceae4', borderRadius: 8, padding: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 700, color: STAGE_COLOR[stage], padding: '2px 4px 8px' }}><span>{stage}</span><span>{items.length}</span></div>
              {items.map((l) => (
                <div key={l.id} style={{ background: '#fff', border: '1px solid #eceae4', borderRadius: 6, padding: 8, marginBottom: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1a2e' }}>{l.name}</div>
                  {l.contact && <div style={{ fontSize: 10, color: '#9a9488' }}>{l.contact}</div>}
                  {l.value ? <div style={{ fontSize: 11, color: '#3d7a52', marginTop: 2 }}>{fmtMoney(l.value)}/mo</div> : null}
                  <div style={{ display: 'flex', gap: 4, marginTop: 6, alignItems: 'center' }}>
                    <select value={l.stage || 'Lead'} onChange={(e) => move(l, e.target.value)} style={{ fontSize: 10, padding: '2px 4px', border: '1px solid #e0ddd8', borderRadius: 4 }}>
                      {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button onClick={() => setEditing(l)} style={miniBtn}>Edit</button>
                    <button onClick={() => remove(l.id)} style={{ ...miniBtn, color: '#b33' }}>✕</button>
                  </div>
                </div>
              ))}
              {items.length === 0 && <div style={{ fontSize: 10, color: '#c2bdb2', padding: 6 }}>—</div>}
            </div>
          );
        })}
      </div>
      {editing && <LeadModal lead={editing} busy={busy} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}

function LeadModal({ lead, busy, onSave, onClose }) {
  const [d, setD] = useState({ name: '', contact: '', email: '', phone: '', value: '', notes: '', stage: 'Lead', ...lead });
  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>{lead.id ? 'Edit lead' : 'New lead'}</div>
        <Field label="Company / name"><input value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} style={inp} /></Field>
        <Field label="Contact"><input value={d.contact} onChange={(e) => setD({ ...d, contact: e.target.value })} style={inp} /></Field>
        <Field label="Email"><input value={d.email} onChange={(e) => setD({ ...d, email: e.target.value })} style={inp} /></Field>
        <Field label="Phone"><input value={d.phone} onChange={(e) => setD({ ...d, phone: e.target.value })} style={inp} /></Field>
        <Field label="Est. value (R/month)"><input type="number" value={d.value} onChange={(e) => setD({ ...d, value: e.target.value })} style={inp} /></Field>
        <Field label="Stage"><select value={d.stage} onChange={(e) => setD({ ...d, stage: e.target.value })} style={inp}>{STAGES.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
        <Field label="Notes"><textarea value={d.notes} onChange={(e) => setD({ ...d, notes: e.target.value })} rows={3} style={{ ...inp, resize: 'vertical' }} /></Field>
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button disabled={busy || !(d.name || '').trim()} onClick={() => onSave({ ...d, value: Number(d.value) || 0 })} style={btnPrimary}>{busy ? 'Saving…' : 'Save'}</button>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
